/**
 * 008-reporting-dashboards T008/T009 — `001`'s tile, wired for real (replaces the previous
 * hard-coded "Not wired up" placeholder). The value is every stale tier price (`rows.totalCount`,
 * §9.3's "N tier prices are stale"); `criticalCount` — the API's own count past
 * `varianceThresholdPct` — only escalates it. Nothing is summed or derived here (constitution I). Drills through to
 * `/reports/staleness` (T021), the same `GET /api/catalog/reports/tier-staleness` the report screen
 * reads — the tile and the report can never disagree because neither computes a second count
 * (FR-016, FR-020), matching `StockExceptionsTile.tsx`'s identical pattern. Drawn by `StatusCard`.
 */
import { PiClockDuotone as Clock } from "react-icons/pi";
import { TileBoundary } from "./TileBoundary";
import { StatusCard, UnreadableStatusCard } from "./StatusCard";
import type { TierStalenessReport } from "../../lib/services/catalog";

const icon = <Clock aria-hidden className="size-5" />;

function unreadable(error: unknown) {
  return <UnreadableStatusCard title="Stale tier prices" icon={icon} error={error} fallback="Could not be read — this is not the same as zero stale prices." />;
}

export function StaleTierPricesTile({ resolve }: { resolve: Promise<TierStalenessReport> }) {
  return (
    <TileBoundary resolve={resolve} errorFallback={unreadable}>
      {(report) => (
        <StatusCard
          title="Stale tier prices"
          icon={icon}
          tone={report.criticalCount > 0 ? "destructive" : report.rows.totalCount > 0 ? "warning" : "success"}
          value={report.rows.totalCount}
          caption={`Not yet re-applied. ${report.criticalCount} past ${report.varianceThresholdPct}% drift from base rate.`}
          link={{ to: "/reports/staleness", label: "Open the staleness report" }}
        />
      )}
    </TileBoundary>
  );
}
